import { useCallback, useEffect, useState } from "react";
import { Building2, Clock3, MapPin, RefreshCw, Users } from "lucide-react";
import { apiGet } from "../lib/api";

interface OnSiteWorkspace {
  workspace: { id: string; name: string; slug: string };
  total: number;
  locations: { locationId: string; locationName: string; count: number }[];
}

interface RecentArrival {
  id: string;
  visitorName: string;
  maskedNric: string;
  checkInAt: string;
  checkOutAt: string | null;
  workspace: { id: string; name: string; slug: string };
  location: { id: string; name: string };
}

export function AllSites() {
  const [sites, setSites] = useState<OnSiteWorkspace[]>([]);
  const [arrivals, setArrivals] = useState<RecentArrival[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [onSite, recent] = await Promise.all([
      apiGet<OnSiteWorkspace[]>("/workspaces/on-site"),
      apiGet<RecentArrival[]>("/workspaces/recent?pageSize=50"),
    ]);
    if (onSite.success) setSites(onSite.data ?? []);
    if (recent.success) setArrivals(recent.data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);

  const total = sites.reduce((sum, site) => sum + site.total, 0);

  return (
    <div className="min-h-full bg-stone-950 p-4 text-white sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-7 flex items-end justify-between">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-brand-300">Live operations</p>
            <h1 className="font-display text-2xl font-semibold sm:text-3xl">All sites</h1>
            <p className="mt-1 text-sm text-stone-400">Current occupancy across every workspace.</p>
          </div>
          <button type="button" onClick={() => void load()} className="rounded-lg p-2 text-stone-400 hover:bg-white/10 hover:text-white" title="Refresh"><RefreshCw size={18} /></button>
        </div>

        <div className="mb-6 grid gap-4 lg:grid-cols-[260px_1fr]">
          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-6">
            <div className="flex items-center gap-2 text-stone-400"><Users size={18} /><span className="text-sm">On site now</span></div>
            <p className="mt-4 text-6xl font-semibold tabular-nums tracking-tight text-white">{loading ? "—" : total}</p>
            <p className="mt-3 text-xs text-stone-500">Across {sites.length} workspace{sites.length === 1 ? "" : "s"}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {sites.map((site) => (
              <a key={site.workspace.id} href={`/w/${site.workspace.slug}/checkins`} className="rounded-2xl border border-white/10 bg-white/[0.06] p-4 transition hover:border-brand-400/50 hover:bg-white/[0.09]">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0"><p className="truncate font-medium text-stone-100">{site.workspace.name}</p><p className="mt-1 text-xs text-stone-500">{site.locations.length} active location{site.locations.length === 1 ? "" : "s"}</p></div>
                  <span className={`text-3xl font-semibold tabular-nums ${site.total ? "text-emerald-400" : "text-stone-500"}`}>{site.total}</span>
                </div>
                <div className="mt-4 space-y-1.5 border-t border-white/10 pt-3">
                  {site.locations.slice(0, 3).map((location) => <div key={location.locationId} className="flex items-center justify-between text-xs"><span className="truncate text-stone-400">{location.locationName}</span><span className="ml-3 tabular-nums text-stone-200">{location.count}</span></div>)}
                  {site.locations.length === 0 && <p className="text-xs text-stone-600">No active locations</p>}
                </div>
              </a>
            ))}
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04]">
          <div className="flex items-center gap-2 border-b border-white/10 px-5 py-4"><Clock3 size={17} className="text-brand-300" /><h2 className="font-medium">Recent arrivals</h2></div>
          <div className="divide-y divide-white/10">
            {arrivals.map((arrival) => (
              <div key={arrival.id} className="grid gap-2 px-5 py-3.5 sm:grid-cols-[minmax(180px,1.4fr)_minmax(160px,1fr)_minmax(140px,1fr)_auto] sm:items-center">
                <div><p className="font-medium text-stone-100">{arrival.visitorName}</p><p className="mt-0.5 text-xs text-stone-500">{arrival.maskedNric}</p></div>
                <div className="flex items-center gap-2 text-sm text-stone-300"><Building2 size={14} className="text-stone-500" /><span className="truncate">{arrival.workspace.name}</span></div>
                <div className="flex items-center gap-2 text-sm text-stone-400"><MapPin size={14} className="text-stone-600" /><span className="truncate">{arrival.location.name}</span></div>
                <div className="text-xs tabular-nums text-stone-500">{new Date(arrival.checkInAt).toLocaleString([], { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}</div>
              </div>
            ))}
            {!loading && arrivals.length === 0 && <p className="px-5 py-10 text-center text-sm text-stone-500">No recent arrivals.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
